import { useState, useEffect } from 'react'
import { FaChevronRight, FaSignOutAlt, FaStar, FaCheckCircle, FaClock, FaUser, FaLock, FaMapMarkerAlt, FaCog } from 'react-icons/fa'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Button from '@/components/common/Button/Button'
import AuthRequired from '@/components/common/AuthRequired/AuthRequired'
import Spinner from '@/components/common/Spinner/Spinner'
import useAuthStore, { type UserDetails } from '@/stores/AuthStore'
import { useProfileStatus } from '@/hooks/useProfile'
import routes from '@/routes/routes'
import './Profile.css'

const Profile = () => {
    const navigate = useNavigate()
    const { isLogged, isLoading, logOut, getUserDetails } = useAuthStore()
    const { t } = useTranslation('common')
    const { data: profileStatus } = useProfileStatus()
    
    const [userDetails, setUserDetails] = useState<UserDetails | undefined>(undefined)
    const [profileLoading, setProfileLoading] = useState(true)
    
    useEffect(() => {
        if (isLogged) {
            setProfileLoading(true)
            getUserDetails().then(userData => {
                setUserDetails(userData)
                setProfileLoading(false)
            })
        }
    }, [isLogged, getUserDetails])
    
    const handleLogout = async () => {
        await logOut()
        navigate('/')
    }
    
    if (!isLogged) {
        return <AuthRequired/>
    }
    
    if (isLoading || profileLoading) {
        return (
            <div className="profile-page">
                <div className="profile-loading">
                    <Spinner/>
                    <p>{t('loading')}</p>
                </div>
            </div>
        )
    }
    
    if (!userDetails) {
        return (
            <div className="profile-page">
                <div className="profile-error">
                    <p>{t('profile.error', 'Failed to load profile data')}</p>
                    <Button onClick={() => window.location.reload()}>
                        {t('common.retry', 'Reintentar')}
                    </Button>
                </div>
            </div>
        )
    }
    
    const isProvider = userDetails?.user_type === 'provider'

    const menuItems = [
        { icon: <FaUser />, label: 'Editar perfil', onClick: () => toast.error('Próximamente') },
        { icon: <FaMapMarkerAlt />, label: 'Mis direcciones', onClick: () => toast.error('Próximamente') },
        { icon: <FaLock />, label: 'Cambiar contraseña', onClick: () => navigate(routes.changePassword) },
        { icon: <FaStar />, label: 'Mis reseñas', onClick: () => toast.error('Próximamente') },
        { icon: <FaCog />, label: 'Configuración', onClick: () => toast.error('Próximamente') },
    ]

    return (
        <div className="profile-page">
            {/* Header Section */}
            <div className="profile-header">
                <div className="profile-header-content">
                    <h1 className="profile-title">Mi Perfil</h1>
                    <p className="profile-subtitle">{isProvider ? 'Panel de Proveedor' : 'Panel de Cliente'}</p>
                </div>
            </div>
            
            <div className="profile-container">

                {/* User Card */}
                <div className="profile-user-card">
                    <div className="profile-avatar">
                        {userDetails.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="profile-user-info">
                        <div className="profile-user-name">{userDetails.email}</div>
                        <div className="profile-user-role">
                            {isProvider ? '👨‍💼 Proveedor' : '👤 Cliente'}
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="profile-stats-grid">
                    <div className="profile-stat-card">
                        <FaStar className="profile-stat-icon" />
                        <div className="profile-stat-value">0.0</div>
                        <div className="profile-stat-label">Calificación</div>
                    </div>
                    <div className="profile-stat-card">
                        <FaStar className="profile-stat-icon" />
                        <div className="profile-stat-value">0</div>
                        <div className="profile-stat-label">Reseñas</div>
                    </div>
                    {isProvider && (
                        <div className="profile-stat-card">
                            <FaCheckCircle className="profile-stat-icon" />
                            <div className="profile-stat-value">0</div>
                            <div className="profile-stat-label">Completados</div>
                        </div>
                    )}
                </div>

                {/* Verification for providers */}
                {isProvider && (
                    <div className={`profile-verification-card ${profileStatus?.is_verified ? 'verified' : 'pending'}`}>
                        <div className="profile-verification-header">
                            {profileStatus?.is_verified ? <FaCheckCircle className="profile-verification-icon" /> : <FaClock className="profile-verification-icon" />}
                            <div>
                                <div className="profile-verification-title">Estado de Verificación</div>
                                <div className="profile-verification-status">
                                    {profileStatus?.is_verified ? 'Verificado' : 'Pendiente'}
                                </div>
                            </div>
                        </div>
                        <p className="profile-verification-text">
                            {profileStatus?.is_verified 
                                ? 'Tu cuenta está verificada. Tus servicios son visibles para los clientes.' 
                                : 'Sube tu Certificado Único Laboral para verificar tu cuenta.'}
                        </p>
                        <Button 
                            variant="primary" 
                            size="md" 
                            onClick={() => navigate(routes.providerSetup)}
                            className="profile-verification-button"
                        >
                            <FaCog /> Ir a Configuración
                        </Button>
                    </div>
                )}

                {/* Menu */}
                <div className="profile-menu-section">
                    <h2 className="profile-section-title">Configuración</h2>
                    <div className="profile-menu-list">
                        {menuItems.map((item, idx) => (
                            <button key={idx} className="profile-menu-item" onClick={item.onClick}>
                                <div className="profile-menu-item-content">
                                    <span className="profile-menu-icon">{item.icon}</span>
                                    <span className="profile-menu-label">{item.label}</span>
                                </div>
                                <FaChevronRight className="profile-menu-arrow" />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Logout */}
                <Button variant="danger" size="lg" onClick={handleLogout} className="profile-logout-btn">
                    <FaSignOutAlt /> Cerrar Sesión
                </Button>

                <div className="profile-version">Domber v1.0.0</div>
            </div>
        </div>
    )
}

export default Profile
